import { useEffect, useState } from 'react';
import { Pencil, Loader2, X, Check } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface StaffRow {
  id: string;
  given_names: string | null;
  family_name: string | null;
  email: string;
}

interface Props {
  matterId: string;
  /** Which DB column to write to. */
  column: 'assigned_staff_id' | 'reviewer_staff_id';
  /** The currently-assigned contact id (so we can highlight it in the picker). */
  currentStaffId: string | null;
  onSaved: () => void;
}

/**
 * Tiny inline picker that lets a staff member reassign a matter's primary
 * assignee or its reviewer slot. Opens a dropdown of all staff contacts on
 * click, writes the change, calls onSaved() so the parent refetches the
 * matter row. Audit log captures the change automatically via the existing
 * fn_audit_changes trigger on public.matters.
 */
export default function AssignStaffEdit({ matterId, column, currentStaffId, onSaved }: Props) {
  const [open, setOpen] = useState(false);
  const [staffList, setStaffList] = useState<StaffRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const { data, error: err } = await supabase
        .from('contacts')
        .select('id, given_names, family_name, email')
        .eq('contact_type', 'staff')
        .is('deleted_at', null)
        .order('family_name', { ascending: true });
      if (cancelled) return;
      if (err) setError(err.message);
      else setStaffList((data ?? []) as StaffRow[]);
    })();
    return () => { cancelled = true; };
  }, [open]);

  const handlePick = async (newId: string | null) => {
    setError(null);
    setSaving(true);
    try {
      const patch: Record<string, string | null> = { [column]: newId };
      const { error: err } = await supabase
        .from('matters')
        .update(patch as never)
        .eq('id', matterId);
      if (err) throw err;
      setOpen(false);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update assignment.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-slate-400 hover:text-[#2CABEA]"
        title={column === 'reviewer_staff_id' ? 'Change reviewer' : 'Change assignee'}
        aria-label="Edit assignment"
      >
        <Pencil size={11} />
      </button>
    );
  }

  return (
    <div className="mt-1 rounded-md border border-slate-200 bg-white p-2 space-y-1.5 text-xs">
      <div className="flex items-center justify-between mb-1">
        <span className="font-semibold text-slate-600">
          {column === 'reviewer_staff_id' ? 'Reassign reviewer' : 'Reassign matter to'}
        </span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-slate-400 hover:text-slate-600"
        >
          <X size={12} />
        </button>
      </div>
      {error && <div className="text-red-700">{error}</div>}
      <div className="max-h-48 overflow-y-auto -mx-1 px-1">
        {staffList.length === 0 ? (
          <div className="text-slate-400 py-1">Loading…</div>
        ) : (
          <ul className="space-y-0.5">
            <li>
              <button
                type="button"
                disabled={saving || currentStaffId === null}
                onClick={() => handlePick(null)}
                className={`w-full text-left px-2 py-1.5 rounded hover:bg-slate-50 disabled:opacity-50 ${currentStaffId === null ? 'bg-slate-100 font-semibold' : ''}`}
              >
                Unassigned
              </button>
            </li>
            {staffList.map((s) => {
              const name = [s.given_names, s.family_name].filter(Boolean).join(' ').trim() || s.email;
              const active = s.id === currentStaffId;
              return (
                <li key={s.id}>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => handlePick(s.id)}
                    className={`w-full text-left px-2 py-1.5 rounded hover:bg-[#EBF7FD] disabled:opacity-50 inline-flex items-center justify-between ${active ? 'bg-[#EBF7FD] font-semibold text-[#0B2341]' : ''}`}
                  >
                    <span>{name}</span>
                    {active && <Check size={12} className="text-[#2CABEA]" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {saving && (
        <div className="flex items-center gap-1.5 text-slate-500">
          <Loader2 size={11} className="animate-spin" /> Saving…
        </div>
      )}
    </div>
  );
}
